import React, { useEffect, useRef, useState } from "react";
import { createNonprofit } from "../../../api/CreateNonprofit";
import { useNavigate } from "react-router-dom";
import TermsModal from "../TermsModal/TermsModal";
import { errorHandler } from "../../shared/Utils/ErrorHandler";
import { lineBreaks } from "../../shared/Utils/LineBreaks";
import SharedTooltip from "../../shared/Tooltip/SharedTooltip";
import SecondaryButton from "../../shared/Buttons/SecondaryButton";

const inputClass =
  "w-full px-4 py-2.5 border border-gray-300 rounded-full text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300";
const labelClass = "block !font-bold !text-sm text-gray-800 mb-1 text-left";
const selectClass =
  "w-full px-4 py-2.5 border border-gray-300 rounded-full text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 appearance-none cursor-pointer";

function NonprofitCreate() {
  const MAX_SIZE = 3 * 1024 * 1024;
  const [formData, setFormData] = useState({
    org_name: "",
    ein_num: "",
    street: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    phone: "",
    mission_stmt: "",
    logo: null,
    instagram_url: "",
    facebook_url: "",
    website_url: "",
    amazon_wishlist_url: "",
    chewy_url: "",
    tiktok_url: "",
  });
  const [urlErrors, setUrlErrors] = useState({
    instagram_url: "",
    facebook_url: "",
    website_url: "",
    amazon_wishlist_url: "",
    chewy_url: "",
    tiktok_url: "",
  });
  const [createError, setCreateError] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);
  const [showTosModal, setShowTosModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (showTosModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [showTosModal]);

  const validateUrl = (url) => {
    const pattern =
      /^https:\/\/www\.[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z0-9-]+)?(?:\/[^\s]*)?$/;
    return url && pattern.test(url);
  };

  const addHttps = (url) => {
    if (url && !url.startsWith("https://")) {
      url = "https://" + url;
    }
    return url;
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > MAX_SIZE) {
      setCreateError("File size is too large (must be under 3MB)");
      setFormData((prev) => ({ ...prev, logo: null }));
    } else {
      setCreateError("");
      setFormData((prev) => ({ ...prev, logo: file }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "logo") {
      handleFileSelect(e.target.files[0]);
    } else if (
      ["instagram_url", "facebook_url", "website_url", "amazon_wishlist_url", "tiktok_url", "chewy_url"].includes(name)
    ) {
      setFormData((prev) => ({ ...prev, [name]: value.trim() }));
      if (value.trim() === "") {
        setUrlErrors((prev) => ({ ...prev, [name]: "" }));
      } else {
        const valid = validateUrl(addHttps(value.trim()));
        setUrlErrors((prev) => ({
          ...prev,
          [name]: valid ? "" : "Invalid URL format. Expected format: https://www.example.com",
        }));
      }
    } else if (name === "phone") {
      if (value.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files[0]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const submitData = new FormData();
    if (formData.logo) submitData.append("logo", formData.logo);
    submitData.append("org_name", formData.org_name);
    submitData.append("ein_num", formData.ein_num);
    submitData.append(
      "address",
      JSON.stringify({
        street: formData.street,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zip_code,
        country: formData.country,
      })
    );
    submitData.append("phone", formData.phone);
    submitData.append("mission_stmt", formData.mission_stmt);

    const correctUrl = (url) =>
      url && !url.startsWith("https://") && !url.startsWith("http://")
        ? "https://" + url
        : url;

    if (formData.instagram_url) submitData.append("instagram_url", correctUrl(formData.instagram_url));
    if (formData.facebook_url) submitData.append("facebook_url", correctUrl(formData.facebook_url));
    if (formData.website_url) submitData.append("website_url", correctUrl(formData.website_url));
    if (formData.amazon_wishlist_url) submitData.append("amazon_wishlist_url", correctUrl(formData.amazon_wishlist_url));
    if (formData.chewy_url) submitData.append("chewy_url", correctUrl(formData.chewy_url));
    if (formData.tiktok_url) submitData.append("tiktok_url", correctUrl(formData.tiktok_url));
    submitData.append("tos", true);

    try {
      await createNonprofit(submitData);
      window.location.reload();
    } catch (error) {
      const errorMessage = errorHandler(error);
      setCreateError(lineBreaks(errorMessage));
      console.error("Error during nonprofit creation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleTosModal = () => setShowTosModal(!showTosModal);
  const handleAcceptTos = () => {
    setTosAccepted(true);
    setShowTosModal(false);
  };

  return (
    <div className="flex-1 bg-gray-100 min-h-screen p-10">
      <form
        onSubmit={handleSubmit}
        encType="multipart/form-data"
        className="bg-white rounded-2xl shadow-md p-10 max-w-3xl mx-auto"
      >
        <h2 className="text-center !text-2xl !font-semibold text-orange mb-8">
          Create a Nonprofit
        </h2>

        {/* Row 1: Org Name, EIN, Phone */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          <div>
            <label className={labelClass}>
              Organization Name <span className="text-red-500">*</span>
            </label>
            <input type="text" name="org_name" value={formData.org_name} onChange={handleChange} placeholder="E.g. Sponsor a Pet" required className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>
              EIN <span className="text-red-500">*</span>
            </label>
            <input type="number" name="ein_num" value={formData.ein_num} onChange={handleChange} placeholder="E.g. 123456789" required className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input type="number" name="phone" value={formData.phone} onChange={handleChange} placeholder="E.g. 1234567890" required className={inputClass} />
          </div>
        </div>

        {/* Row 2: Street, City */}
        <div className="grid grid-cols-2 gap-4 mb-5">
          <div>
            <label className={labelClass}>
              Street Address <span className="text-red-500">*</span>
            </label>
            <input type="text" name="street" value={formData.street} onChange={handleChange} placeholder="E.g. 188 King St." className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>
              City <span className="text-red-500">*</span>
            </label>
            <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder="E.g. San Francisco" className={inputClass} />
          </div>
        </div>

        {/* Row 3: State, Country, Zip */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          <div>
            <label className={labelClass}>
              State <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select name="state" value={formData.state} onChange={handleChange} className={selectClass}>
                <option value="" disabled>Select State</option>
                {["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>
              Country <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <select name="country" value={formData.country} onChange={handleChange} className={selectClass}>
                <option value="" disabled>Select Country</option>
                <option value="US">US</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </div>
            </div>
          </div>
          <div>
            <label className={labelClass}>
              Zip Code <span className="text-red-500">*</span>
            </label>
            <input type="number" name="zip_code" value={formData.zip_code} onChange={handleChange} placeholder="E.g. 94107" className={inputClass} />
          </div>
        </div>

        {/* Mission Statement */}
        <div className="mb-5">
          <label className={labelClass}>
            Mission Statement <span className="text-red-500">*</span>
          </label>
          <textarea
            name="mission_stmt"
            value={formData.mission_stmt}
            onChange={handleChange}
            placeholder="Enter your mission statement..."
            rows={6}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-2xl text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
          />
        </div>

        {/* URL Fields */}
        {[
          ["instagram_url", "Instagram URL", "E.g. https://www.instagram.com", "facebook_url", "Facebook URL", "E.g. https://www.facebook.com"],
          ["website_url", "Website URL", "E.g. https://www.sponsorapet.com", "amazon_wishlist_url", "Amazon Wishlist URL", "E.g. https://www.amazon.com"],
          ["tiktok_url", "TikTok URL", "E.g. https://www.tiktok.com", "chewy_url", "Chewy URL", "E.g. https://www.chewy.com"],
        ].map(([name1, label1, ph1, name2, label2, ph2]) => (
          <div key={name1} className="grid grid-cols-2 gap-4 mb-5">
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className={labelClass}>{label1}</label>
                <SharedTooltip id="my-tooltip" content="Example URL: (www.example.com or https://www.example.com)" aClass="mb-0.5 cursor-pointer" toolClass="ml-1" />
              </div>
              <input type="text" name={name1} value={formData[name1]} onChange={handleChange} placeholder={ph1} className={inputClass} />
              {urlErrors[name1] && <p className="text-red-500 text-xs mt-1">{urlErrors[name1]}</p>}
            </div>
            <div>
              <div className="flex items-center gap-1 mb-1">
                <label className={labelClass}>{label2}</label>
                <SharedTooltip id="my-tooltip" content="Example URL: (www.example.com or https://www.example.com)" aClass="mb-0.5 cursor-pointer" toolClass="ml-1" />
              </div>
              <input type="text" name={name2} value={formData[name2]} onChange={handleChange} placeholder={ph2} className={inputClass} />
              {urlErrors[name2] && <p className="text-red-500 text-xs mt-1">{urlErrors[name2]}</p>}
            </div>
          </div>
        ))}

        {/* Organization Logo */}
        <div className="mb-5">
          <label className={labelClass}>
            Organization Logo <span className="text-red-500">*</span>
          </label>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
            className={`border-2 border-dashed rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer transition-colors ${dragOver ? "border-blue-400 bg-blue-50" : "border-gray-300 bg-gray-50"}`}
          >
            <svg className="w-8 h-8 text-blue mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            <p className="text-sm text-gray-700">
              Drag &amp; drop files or{" "}
              <span className="text-blue underline font-medium">choose file</span>{" "}
              to upload
            </p>
            <p className="text-xs text-gray-400 mt-1">Supported formates: JPEG, PNG, PSD</p>
            <input ref={fileInputRef} type="file" name="logo" onChange={handleChange} className="hidden" accept=".jpg,.jpeg,.png,.psd" />
          </div>

          {/* File preview */}
          {formData.logo && (
            <div className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3 mt-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-200 rounded-lg flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-gray-800">{formData.logo.name}</p>
                  <p className="!text-[12px] text-blue text-left">Uploaded ✓</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, logo: null }))}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {createError && <p className="text-red-500 text-sm text-center mb-3">{createError}</p>}

        {/* Terms and Services */}
        <button
          type="button"
          onClick={toggleTosModal}
          className={`w-full py-3.5 rounded-full font-bold text-white text-sm mb-3 transition-colors ${tosAccepted ? "bg-green-500" : "bg-blue hover:bg-blue-dark3"}`}
        >
          Terms and Services <span className="text-red-300">*</span>
        </button>

        {/* Create Nonprofit */}
        <button
          type="submit"
          disabled={!formData.logo || formData.logo.size > MAX_SIZE || !tosAccepted || isLoading}
          className="w-full py-3.5 rounded-full font-bold text-gray-800 text-sm border-2 border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Create Nonprofit
        </button>
      </form>

      {showTosModal && (
        <TermsModal isOpen={showTosModal} onAccept={handleAcceptTos} onClose={toggleTosModal} />
      )}
    </div>
  );
}

export default NonprofitCreate;
